import React, { useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface GenericModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: number | string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  topRightActions?: React.ReactNode;
}

const GenericModal: React.FC<GenericModalProps> = ({ 
  open, 
  onClose,
  children,
  maxWidth = 800,
  ariaLabelledBy = "modal-title",
  ariaDescribedBy = "modal-description",
  topRightActions,
}) => {
  const openedAtRef = useRef(0);
  const touchStartTimeRef = useRef<number | null>(null);
  const suppressNextBackdropClickRef = useRef(false);

  useEffect(() => {
    if (open) {
      openedAtRef.current = Date.now();

      const previousUserSelect = document.body.style.userSelect;
      const previousWebkitUserSelect = document.body.style.webkitUserSelect;
      const previousTouchCallout = (document.body.style as CSSStyleDeclaration & {
        webkitTouchCallout?: string;
      }).webkitTouchCallout;

      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      (document.body.style as CSSStyleDeclaration & { webkitTouchCallout?: string; }).webkitTouchCallout = 'none';

      return () => {
        document.body.style.userSelect = previousUserSelect;
        document.body.style.webkitUserSelect = previousWebkitUserSelect;
        (document.body.style as CSSStyleDeclaration & { webkitTouchCallout?: string; }).webkitTouchCallout = previousTouchCallout ?? '';
      };
    }
  }, [open]);

  const handleModalTouchStart = () => {
    touchStartTimeRef.current = Date.now();
  };

  const handleModalTouchEnd = (event: React.TouchEvent) => {
    const startedAt = touchStartTimeRef.current;
    touchStartTimeRef.current = null;

    if (startedAt && Date.now() - startedAt >= 500) {
      suppressNextBackdropClickRef.current = true;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (suppressNextBackdropClickRef.current) {
      suppressNextBackdropClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  };

  const handleModalClose = (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (suppressNextBackdropClickRef.current) {
      suppressNextBackdropClickRef.current = false;
      return;
    }

    // Guard against the opening click being interpreted as an immediate backdrop click.
    if (reason === 'backdropClick' && Date.now() - openedAtRef.current < 250) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      onClick={handleModalClick}
      onTouchStart={handleModalTouchStart}
      onTouchEnd={handleModalTouchEnd}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
          maxWidth,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'auto',
          '&:focus-visible': {
            outline: 'none',
          },
        }}
      >
        <Card elevation={0} sx={{ border: 'none' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Close button in top right */}
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ position: 'absolute', top: 16, right: 16, gap: 1 }}
            >
              {topRightActions}
              <IconButton
                onClick={onClose}
                size="large"
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Modal content */}
            {children}
          </CardContent>
        </Card>
      </Box>
    </Modal>
  );
};

export default GenericModal;
